import React, { useState, useEffect } from 'react';
import { Wallet } from 'lucide-react';
import { SEO } from '../components/SEO';

interface LoanState {
  amount: string;
  interestRate: string;
  loanTerm: string;
  paymentFrequency: 'monthly' | 'quarterly' | 'yearly';
  currency: 'EUR' | 'BAM' | 'RSD';
}

interface AmortizationRow {
  payment: number;
  principal: number;
  interest: number;
  remainingBalance: number;
}

const currencySymbols: Record<string, string> = {
  EUR: '€',
  BAM: 'KM',
  RSD: 'RSD'
};

function KalkulatorKredita() {
  const [values, setValues] = useState<LoanState>({
    amount: '',
    interestRate: '',
    loanTerm: '',
    paymentFrequency: 'monthly',
    currency: 'EUR'
  });

  const [results, setResults] = useState({
    monthlyPayment: 0,
    totalPayment: 0,
    totalInterest: 0,
    amortizationSchedule: [] as AmortizationRow[]
  });

  const [showAmortization, setShowAmortization] = useState(false);

  const calculateLoan = () => {
    const principal = parseFloat(values.amount);
    const annualRate = parseFloat(values.interestRate) / 100;
    const years = parseFloat(values.loanTerm);

    if (isNaN(principal) || isNaN(annualRate) || isNaN(years)) {
      return;
    }

    let numberOfPayments: number;
    let periodicRate: number;

    switch (values.paymentFrequency) {
      case 'monthly':
        numberOfPayments = years * 12;
        periodicRate = annualRate / 12;
        break;
      case 'quarterly':
        numberOfPayments = years * 4;
        periodicRate = annualRate / 4;
        break;
      case 'yearly':
        numberOfPayments = years;
        periodicRate = annualRate;
        break;
      default:
        numberOfPayments = years * 12;
        periodicRate = annualRate / 12;
    }

    // Calculate monthly payment using the loan payment formula
    const payment = principal * (periodicRate * Math.pow(1 + periodicRate, numberOfPayments)) / 
                   (Math.pow(1 + periodicRate, numberOfPayments) - 1);

    // Calculate total payment and interest
    const totalPayment = payment * numberOfPayments;
    const totalInterest = totalPayment - principal;

    // Generate amortization schedule
    const schedule: AmortizationRow[] = [];
    let balance = principal;
    
    for (let i = 1; i <= numberOfPayments; i++) {
      const interest = balance * periodicRate;
      const principalPart = payment - interest;
      balance -= principalPart;

      schedule.push({
        payment: i,
        principal: principalPart,
        interest: interest,
        remainingBalance: Math.max(0, balance)
      });
    }

    setResults({
      monthlyPayment: parseFloat(payment.toFixed(2)),
      totalPayment: parseFloat(totalPayment.toFixed(2)),
      totalInterest: parseFloat(totalInterest.toFixed(2)),
      amortizationSchedule: schedule
    });
  };

  const handleInputChange = (field: keyof LoanState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.amount && values.interestRate && values.loanTerm) {
      calculateLoan();
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Kredita",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte rate kredita, ukupne troškove i plan otplate. Precizni izračuni s detaljnim prikazom amortizacije kredita.",
    "url": "https://online-kalkulator.com/kalkulator-kredita",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun rate kredita",
      "Plan otplate kredita",
      "Izračun ukupnih troškova",
      "Viševalutna podrška",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Kredita | Izračun Rate Kredita"
        description="Izračunajte ratu kredita, ukupne troškove i plan otplate. Precizni izračuni s detaljnim prikazom amortizacije kredita za različite valute."
        canonicalUrl="/kalkulator-kredita"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Wallet className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Kredita</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Iznos kredita
                  </label>
                  <input
                    type="number"
                    value={values.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite iznos"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Godišnja kamatna stopa (%)
                  </label>
                  <input
                    type="number"
                    value={values.interestRate}
                    onChange={(e) => handleInputChange('interestRate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite kamatnu stopu"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rok otplate (godine)
                  </label>
                  <input
                    type="number"
                    value={values.loanTerm}
                    onChange={(e) => handleInputChange('loanTerm', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Unesite rok otplate"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valuta
                  </label>
                  <select
                    value={values.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="EUR">Euro (€)</option>
                    <option value="BAM">Konvertibilna marka (KM)</option>
                    <option value="RSD">Srpski dinar (RSD)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Učestalost otplate
                </label>
                <select
                  value={values.paymentFrequency}
                  onChange={(e) => handleInputChange('paymentFrequency', e.target.value as LoanState['paymentFrequency'])}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="monthly">Mjesečno</option>
                  <option value="quarterly">Kvartalno</option>
                  <option value="yearly">Godišnje</option>
                </select>
              </div>

              {/* Results */}
              {results.monthlyPayment > 0 && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Rata:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.monthlyPayment)} {currencySymbols[values.currency]}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Ukupna kamata:</span>
                    <span className="text-lg font-semibold text-gray-800">
                      {formatCurrency(results.totalInterest)} {currencySymbols[values.currency]}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Ukupno za otplatu:</span>
                    <span className="text-lg font-semibold text-gray-800">
                      {formatCurrency(results.totalPayment)} {currencySymbols[values.currency]}
                    </span>
                  </div>

                  {/* Amortization Schedule Toggle */}
                  <button
                    onClick={() => setShowAmortization(!showAmortization)}
                    className="w-full mt-4 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                  >
                    {showAmortization ? 'Sakrij' : 'Prikaži'} plan otplate
                  </button>

                  {/* Amortization Schedule */}
                  {showAmortization && (
                    <div className="mt-4 overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Rata
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Glavnica
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Kamata
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Preostali dug
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {results.amortizationSchedule.map((row) => (
                            <tr key={row.payment}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {row.payment}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(row.principal)} {currencySymbols[values.currency]}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(row.interest)} {currencySymbols[values.currency]}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatCurrency(row.remainingBalance)} {currencySymbols[values.currency]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Kredita</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kreditni kalkulator je koristan alat koji vam pomaže izračunati mjesečne rate kredita,
                  ukupne troškove i plan otplate. Možete unijeti različite parametre kao što su iznos
                  kredita, kamatna stopa i rok otplate kako biste dobili detaljan pregled troškova i
                  plana otplate.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Značajke</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun mjesečne rate kredita</li>
                  <li>Prikaz ukupnih troškova kamata</li>
                  <li>Detaljan plan otplate (amortizacijska tablica)</li>
                  <li>Podrška za različite valute</li>
                  <li>Prilagodljiva učestalost otplate (mjesečno, kvartalno, godišnje)</li>
                  <li>Automatsko ažuriranje izračuna</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite iznos kredita koji želite podići</li>
                  <li>Postavite godišnju kamatnu stopu (u postotcima)</li>
                  <li>Odredite rok otplate u godinama</li>
                  <li>Odaberite željenu valutu</li>
                  <li>Odaberite učestalost otplate</li>
                  <li>Rezultati će se automatski izračunati</li>
                  <li>Kliknite na "Prikaži plan otplate" za detaljnu amortizacijsku tablicu</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Svi izračuni su informativne prirode</li>
                  <li>Kamatna stopa se unosi kao godišnja stopa</li>
                  <li>Izračuni se temelje na anuitetnoj otplati kredita</li>
                  <li>Plan otplate prikazuje detaljnu raspodjelu svake rate na glavnicu i kamatu</li>
                  <li>Iznosi su zaokruženi na dvije decimale</li>
                  <li>Kalkulator ne uzima u obzir dodatne troškove kredita (naknade, osiguranje i sl.)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Formule za Izračun</h2>
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Mjesečna rata (anuitet) se izračunava prema formuli:
                  </p>
                  <p className="text-gray-800 font-mono bg-gray-50 p-4 rounded-lg">
                    A = P * (r * (1 + r)^n) / ((1 + r)^n - 1)
                  </p>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>A = mjesečna rata (anuitet)</li>
                    <li>P = iznos kredita (glavnica)</li>
                    <li>r = mjesečna kamatna stopa (godišnja stopa / 12)</li>
                    <li>n = ukupan broj rata</li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorKredita;